import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchMyReports, type TransformationReport } from '../../../lib/transformation-reports';
import { MarkdownRenderer } from '../../../components/ai/MarkdownRenderer';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  BarChart2, Calendar, Target, Activity, ClipboardCheck, Trophy,
  BookOpen, ChevronDown, ChevronRight,
} from '../../../components/icons';

const REPORT_TYPE_LABELS: Record<string, string> = {
  weekly: 'Haftalık',
  monthly: 'Aylık',
  programme_end: 'Program Sonu',
};

const REPORT_TYPE_COLORS: Record<string, string> = {
  weekly: 'bg-blue-100 text-blue-700',
  monthly: 'bg-sage-100 text-sage-700',
  programme_end: 'bg-amber-100 text-amber-700',
};

function MiniBar({ value, max = 100, color }: { value: number; max?: number; color: string }) {
  return (
    <div className="flex-1 h-1.5 bg-cream-200 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${(value / max) * 100}%` }} />
    </div>
  );
}

function ReportCard({ report }: { report: TransformationReport }) {
  const [expanded, setExpanded] = useState(false);
  const q = report.quantitative;

  return (
    <div className="freud-card-elevated overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full p-5 text-left flex items-start gap-4 hover:bg-cream-50 transition-colors"
      >
        <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <BarChart2 className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${REPORT_TYPE_COLORS[report.report_type] || 'bg-cream-100 text-chocolate-600'}`}>
              {REPORT_TYPE_LABELS[report.report_type] || report.report_type}
            </span>
          </div>
          <p className="font-semibold text-chocolate-900">{report.title}</p>
          <p className="text-xs text-chocolate-400 mt-0.5 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            {new Date(report.period_start).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })} –{' '}
            {new Date(report.period_end).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-chocolate-400 flex-shrink-0 mt-1" /> : <ChevronRight className="w-4 h-4 text-chocolate-400 flex-shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div className="border-t border-cream-200 p-5 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Aktif Hedefler', value: q.goalsActive, icon: Target, color: 'text-sage-600' },
              { label: 'Alışkanlık Oranı', value: `${q.overallHabitRate}%`, icon: Activity, color: 'text-coral-600' },
              { label: 'Tamamlanan Görev', value: q.tasksCompleted, icon: ClipboardCheck, color: 'text-amber-600' },
              { label: 'Kilometre Taşları', value: q.milestonesInPeriod, icon: Trophy, color: 'text-yellow-600' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-cream-50 rounded-xl p-3 border border-cream-200">
                <Icon className={`w-4 h-4 mb-1 ${color}`} />
                <p className="text-xl font-bold text-chocolate-900">{value}</p>
                <p className="text-[11px] text-chocolate-500">{label}</p>
              </div>
            ))}
          </div>

          {q.checkInStats && q.checkInStats.count > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">Refah Ortalamaları ({q.checkInStats.count} check-in)</h3>
              <div className="space-y-1.5">
                {[
                  { label: 'Enerji', value: q.checkInStats.avgEnergy, color: 'bg-amber-400' },
                  { label: 'Ruh Hali', value: q.checkInStats.avgMood, color: 'bg-coral-400' },
                  { label: 'Odak', value: q.checkInStats.avgFocus, color: 'bg-blue-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-xs text-chocolate-500 w-16">{label}</span>
                    <MiniBar value={value} max={10} color={color} />
                    <span className="text-xs font-semibold text-chocolate-700 w-8 text-right">{value}/10</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {q.milestonesList && q.milestonesList.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">Ulaşılan Kilometre Taşları</h3>
              <div className="space-y-1.5">
                {q.milestonesList.map((m: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Trophy className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                    <span className="flex-1 text-chocolate-700">{m.title}</span>
                    <span className="text-[10px] text-chocolate-400">
                      {new Date(m.achievedAt).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {q.journalExcerpts && q.journalExcerpts.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" />
                Yazılarınız ({q.journalEntriesInPeriod} yazı)
              </h3>
              <div className="space-y-3">
                {q.journalExcerpts.map((j: any, i: number) => (
                  <div key={i} className="border-l-2 border-sage-200 pl-3">
                    <p className="text-[10px] text-chocolate-400 mb-0.5">{new Date(j.date).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })} — {j.title}</p>
                    <p className="text-xs text-chocolate-600 italic">"{j.content}{j.content.length >= 200 ? '…' : ''}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.ai_narrative && (
            <div>
              <h3 className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-3">Dönüşüm Öyküsü</h3>
              <div className="prose-sm max-w-none text-chocolate-700">
                <MarkdownRenderer content={report.ai_narrative} />
              </div>
            </div>
          )}

          {report.coach_notes && (
            <div className="bg-sage-50 rounded-xl p-4 border border-sage-100">
              <h3 className="text-xs font-semibold text-sage-700 uppercase tracking-wide mb-2">Koç Notları</h3>
              <p className="text-sm text-chocolate-700 whitespace-pre-line">{report.coach_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MyReportsTR() {
  const { user } = useAuth();
  const [reports, setReports] = useState<TransformationReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchMyReports(user.id).then(setReports).finally(() => setIsLoading(false));
  }, [user]);

  if (isLoading) return <LoadingSpinner message="Raporlar yükleniyor..." />;

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
          <BarChart2 className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h1 className="freud-heading-xl text-chocolate-900">Raporlarım</h1>
          <p className="text-sm text-chocolate-500">Koçunuzdan dönüşüm raporları</p>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <BarChart2 className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">Henüz rapor yok. Koçunuz dönüşüm raporlarını burada paylaşacak.</p>
          <p className="text-sm text-chocolate-400 mt-2">Raporlar ilerlemenizi, alışkanlıklarınızı, kilometre taşlarınızı ve yapay zekâ destekli içgörüleri gösterir.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <ReportCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
}
